export const POLAND_BOUNDS = [13.7, 48.8, 24.4, 55.1];

export function walkCoordinates(geometry, visitor) {
  if (!geometry) return;
  const visit = (value) => {
    if (Array.isArray(value) && typeof value[0] === 'number' && typeof value[1] === 'number') {
      visitor(value);
      return;
    }
    if (Array.isArray(value)) value.forEach(visit);
  };
  visit(geometry.coordinates);
}

export function geometryBounds(features, fallback = POLAND_BOUNDS) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  features.forEach((feature) => {
    walkCoordinates(feature?.geometry, ([x, y]) => {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });
  });

  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return fallback;
  const dx = Math.max(maxX - minX, 0.02);
  const dy = Math.max(maxY - minY, 0.02);
  return [minX - dx * 0.04, minY - dy * 0.04, maxX + dx * 0.04, maxY + dy * 0.04];
}

export function projectCoord([x, y], bounds, width = 900, height = 620, padding = 28) {
  const [minX, minY, maxX, maxY] = bounds;
  const availableWidth = width - padding * 2;
  const availableHeight = height - padding * 2;
  const scale = Math.min(availableWidth / Math.max(maxX - minX, 0.0001), availableHeight / Math.max(maxY - minY, 0.0001));
  const drawnWidth = (maxX - minX) * scale;
  const drawnHeight = (maxY - minY) * scale;
  const offsetX = (width - drawnWidth) / 2;
  const offsetY = (height - drawnHeight) / 2;

  return [offsetX + (x - minX) * scale, offsetY + (maxY - y) * scale];
}

export function pathForGeometry(geometry, bounds, width = 900, height = 620) {
  if (!geometry) return '';
  const polygons = geometry.type === 'Polygon'
    ? [geometry.coordinates]
    : geometry.type === 'MultiPolygon'
      ? geometry.coordinates
      : [];

  return polygons
    .map((polygon) => polygon
      .map((ring) => {
        const points = ring.map((coordinate) => projectCoord(coordinate, bounds, width, height));
        return `M${points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join('L')}Z`;
      })
      .join(' '))
    .join(' ');
}

function hasCoordinateValue(value) {
  return value !== null && value !== undefined && String(value).trim() !== '' && Number.isFinite(Number(value));
}

export function hasPolandCoordinate(longitude, latitude) {
  if (!hasCoordinateValue(longitude) || !hasCoordinateValue(latitude)) return false;
  const lon = Number(longitude);
  const lat = Number(latitude);
  return lon >= 13 && lon <= 25 && lat >= 48 && lat <= 56;
}

export function coordinateToPoint(longitude, latitude, bounds, width = 900, height = 620) {
  if (!hasPolandCoordinate(longitude, latitude)) return null;
  return projectCoord([Number(longitude), Number(latitude)], bounds, width, height);
}

function clampLatitude(latitude) {
  return Math.max(-85.05112878, Math.min(85.05112878, Number(latitude)));
}

export function lonLatToMercatorWorld(longitude, latitude) {
  const lon = Number(longitude);
  const lat = clampLatitude(latitude);
  const x = (lon + 180) / 360;
  const rad = lat * Math.PI / 180;
  const y = (1 - Math.log(Math.tan(rad) + (1 / Math.cos(rad))) / Math.PI) / 2;
  return [x, y];
}

export function geometryMercatorBounds(features) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  features.forEach((feature) => {
    walkCoordinates(feature?.geometry, ([lon, lat]) => {
      const [x, y] = lonLatToMercatorWorld(lon, lat);
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    });
  });

  if (![minX, minY, maxX, maxY].every(Number.isFinite)) {
    const [minLon, minLat, maxLon, maxLat] = POLAND_BOUNDS;
    const [x1, y2] = lonLatToMercatorWorld(minLon, minLat);
    const [x2, y1] = lonLatToMercatorWorld(maxLon, maxLat);
    return [x1, y1, x2, y2];
  }

  const dx = Math.max(maxX - minX, 0.00002);
  const dy = Math.max(maxY - minY, 0.00002);
  return [minX - dx * 0.075, minY - dy * 0.075, maxX + dx * 0.075, maxY + dy * 0.075];
}

export function mercatorViewport(bounds, width = 900, height = 620, padding = 24) {
  const [minX, minY, maxX, maxY] = bounds;
  const availableWidth = width - padding * 2;
  const availableHeight = height - padding * 2;
  const scale = Math.min(
    availableWidth / Math.max(maxX - minX, 1e-9),
    availableHeight / Math.max(maxY - minY, 1e-9),
  );
  const drawnWidth = (maxX - minX) * scale;
  const drawnHeight = (maxY - minY) * scale;
  const offsetX = (width - drawnWidth) / 2;
  const offsetY = (height - drawnHeight) / 2;
  return { minX, minY, maxX, maxY, scale, offsetX, offsetY };
}

export function mercatorWorldToPoint([x, y], bounds, width = 900, height = 620, padding = 24) {
  const viewport = mercatorViewport(bounds, width, height, padding);
  return [
    viewport.offsetX + (x - viewport.minX) * viewport.scale,
    viewport.offsetY + (y - viewport.minY) * viewport.scale,
  ];
}

export function coordinateToMercatorPoint(longitude, latitude, bounds, width = 900, height = 620, padding = 24) {
  if (!hasPolandCoordinate(longitude, latitude)) return null;
  return mercatorWorldToPoint(lonLatToMercatorWorld(Number(longitude), Number(latitude)), bounds, width, height, padding);
}

export function pathForGeometryMercator(geometry, bounds, width = 900, height = 620, padding = 24) {
  if (!geometry) return '';
  const polygons = geometry.type === 'Polygon'
    ? [geometry.coordinates]
    : geometry.type === 'MultiPolygon'
      ? geometry.coordinates
      : [];

  return polygons
    .map((polygon) => polygon
      .map((ring) => {
        const points = ring.map(([lon, lat]) => coordinateToMercatorPoint(lon, lat, bounds, width, height, padding));
        return `M${points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join('L')}Z`;
      })
      .join(' '))
    .join(' ');
}

export function geometryVisualCenter(geometry, bounds, width = 900, height = 620, mercator = false) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  walkCoordinates(geometry, ([lon, lat]) => {
    const point = mercator
      ? coordinateToMercatorPoint(lon, lat, bounds, width, height)
      : projectCoord([lon, lat], bounds, width, height);
    if (!point) return;
    minX = Math.min(minX, point[0]);
    minY = Math.min(minY, point[1]);
    maxX = Math.max(maxX, point[0]);
    maxY = Math.max(maxY, point[1]);
  });
  if (![minX, minY, maxX, maxY].every(Number.isFinite)) return null;
  return [(minX + maxX) / 2, (minY + maxY) / 2];
}

export function osmTilesForMercatorBounds(bounds, width = 900, height = 620, padding = 24) {
  const viewport = mercatorViewport(bounds, width, height, padding);
  let zoom = Math.floor(Math.log2(Math.max(viewport.scale / 256, 1))) + 1;
  zoom = Math.max(10, Math.min(16, zoom));

  const build = (z) => {
    const n = 2 ** z;
    const minTileX = Math.floor(bounds[0] * n);
    const maxTileX = Math.floor(bounds[2] * n);
    const minTileY = Math.floor(bounds[1] * n);
    const maxTileY = Math.floor(bounds[3] * n);
    const tiles = [];

    for (let tx = minTileX; tx <= maxTileX; tx += 1) {
      for (let ty = minTileY; ty <= maxTileY; ty += 1) {
        if (tx < 0 || tx >= n || ty < 0 || ty >= n) continue;
        const worldLeft = tx / n;
        const worldTop = ty / n;
        const worldRight = (tx + 1) / n;
        const worldBottom = (ty + 1) / n;
        const [x1, y1] = mercatorWorldToPoint([worldLeft, worldTop], bounds, width, height, padding);
        const [x2, y2] = mercatorWorldToPoint([worldRight, worldBottom], bounds, width, height, padding);
        tiles.push({
          key: `${z}/${tx}/${ty}`,
          href: `https://tile.openstreetmap.org/${z}/${tx}/${ty}.png`,
          x: x1,
          y: y1,
          width: x2 - x1,
          height: y2 - y1,
        });
      }
    }
    return tiles;
  };

  let tiles = build(zoom);
  while (tiles.length > 42 && zoom > 9) {
    zoom -= 1;
    tiles = build(zoom);
  }

  return { zoom, tiles };
}
